import { Slice } from "gatsby";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";

import GlobalStyles from "~/styles/GlobalStyles";

import { useLocationContext } from "~/context/LocationContext";
import { useStickyContext } from "~/context/StickyContext";
import { useTranslationContext } from "~/context/TranslationContext";

import transition from "~/utilities/transition";
import { getElementFromHash, scrollToElement } from "~/utilities/helpers";
import { getLocalStorageItem } from "~/utilities/localStorageHelpers";

import CookieBlock from "~/queryBlocks/CookieBlock";

const FullWrapper = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto 1fr auto;
    min-height: 100vh;

    &.overlayIsActive:before {
        ${transition({}, "opacity", "visibility", "top")};
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background-color: rgba(255, 255, 255, 0.87);
        height: 100%;
        z-index: ${({ theme }) => theme.zIndex.headerOverlay};
    }

    & > header {
        grid-row: 1;
    }

    & > main {
        grid-row: 2;
    }

    & > footer {
        grid-row: 3;
    }
`;

const hashLinkScroll = (hash, totalHeight) => {
    if (hash) {
        const element = getElementFromHash(hash);

        if (element) {
            scrollToElement(element, totalHeight);
        } else {
            console.log("no element", { element, hash });
        }
    }
};

const Primary = ({
    children,
    pageCode = "",
    isProductPage = false,
    urlPermutations = [],
}) => {
    const {
        location: { hash },
    } = useLocationContext();

    const { totalHeight } = useStickyContext();

    const { initialize } = useTranslationContext();

    const [hideMain, setHideMain] = useState(true);

    const scrollToHash = useDebouncedCallback(
        (hash, totalHeight) => hashLinkScroll(hash, totalHeight),
        200,
    );

    useEffect(() => {
        initialize(pageCode, urlPermutations);
        if (getLocalStorageItem("cookiesAccepted") === "true") {
            setHideMain(false);
        }
    }, [initialize, pageCode, urlPermutations]);

    useEffect(() => {
        scrollToHash(hash, totalHeight);
    }, [hash, totalHeight, scrollToHash]);

    const onAcceptCookies = () => {
        document.querySelector("#skip-link").focus();
        setHideMain(false);
    };

    return (
        <>
            <CookieBlock closeModalProp={onAcceptCookies} />
            <FullWrapper id="primaryFullWrapper" aria-hidden={hideMain}>
                <GlobalStyles {...{ isProductPage }} />
                <Slice
                    alias="HEADER"
                    pageCode={pageCode}
                    urlPermutations={urlPermutations}
                />
                <main id="mainContent">{children}</main>
                <Slice alias="FOOTER" pageCode={pageCode} />
            </FullWrapper>
        </>
    );
};

Primary.propTypes = {
    pageCode: PropTypes.string,
    children: PropTypes.node.isRequired,
    isProductPage: PropTypes.bool,
    urlPermutations: PropTypes.array,
};

export default Primary;
